import moment from "moment-timezone";

// --- 1️⃣ Data Fetching ---
export async function fetchInventoryData(apiService, dispatch) {
  const [items, transactions] = await Promise.all([
    apiService.get(dispatch, "inventory"),
    apiService.get(dispatch, "inventoryLogs"),
  ]);
  return { items, transactions };
}

export async function fetchInventoryTransactions(apiService, dispatch) {
  return apiService.get(dispatch, "inventoryLogs");
}

// --- 2️⃣ Low Stock ---
export function computeLowStock(items) {
  return items.filter((i) => i.quantity <= i.reorderLevel);
}

// --- 3️⃣ Soon to Expire ---
export function computeSoonToExpire(items) {
  const now = new Date();
  const soonExpireLimit = new Date();
  soonExpireLimit.setDate(now.getDate() + 30);

  return items.filter((i) => {
    if (!i.expiryDate) return false;
    const expiry = new Date(i.expiryDate);
    return expiry >= now && expiry <= soonExpireLimit;
  });
}

// --- 4️⃣ Monthly Transactions ---
export function getThisMonthTransactions(transactions, year, month) {
  const timezone = "Asia/Manila";
  return transactions.filter((t) => {
    const d = moment.tz(t.created_on, timezone);
    return d.year() === year && d.month() === month - 1; // <-- month - 1
  });
}

// --- 5️⃣ Group Items by Category ---
export function groupItemsByCategory(items) {
  const map = {};
  items.forEach((it) => {
    const cat = it.category || "Uncategorized";
    if (!map[cat]) map[cat] = [];
    map[cat].push(it);
  });
  const chartData = Object.entries(map).map(([name, arr]) => ({
    name,
    count: arr.length,
  }));
  return { map, chartData };
}

// --- 6️⃣ Build Monthly Item Aggregation ---
export function buildItemMonthlyMap(items, thisMonthTransactions) {
  const itemMonthlyMap = {};
  items.forEach((it) => {
    const name = it.name || "Unknown";
    itemMonthlyMap[name] = {
      name,
      category: it.category || "Uncategorized",
      stockIn: 0,
      stockOut: 0,
    };
  });

  thisMonthTransactions
    .sort((a, b) => new Date(b.created_on) - new Date(a.created_on))
    .forEach((t) => {
      const name = t.item?.name || "Unknown";
      if (!itemMonthlyMap[name]) {
        itemMonthlyMap[name] = {
          name,
          category: t.item?.category || "Uncategorized",
          stockIn: 0,
          stockOut: 0,
        };
      }
      if (t.type === "Stock in") itemMonthlyMap[name].stockIn += t.quantity;
      if (t.type === "Stock out") itemMonthlyMap[name].stockOut += t.quantity;
    });

  return itemMonthlyMap;
}

// --- 7️⃣ Top 5 Moved Items ---
export function computeTopMovedItems(transactions) {
  const movementMap = {};
  transactions.forEach((t) => {
    const name = t.item?.name || "Unknown";
    if (!movementMap[name])
      movementMap[name] = { name, stockIn: 0, stockOut: 0 };
    if (t.type === "Stock in") movementMap[name].stockIn += t.quantity;
    if (t.type === "Stock out") movementMap[name].stockOut += t.quantity;
  });

  return Object.values(movementMap)
    .sort((a, b) => b.stockIn + b.stockOut - (a.stockIn + a.stockOut))
    .slice(0, 5);
}

// --- 8️⃣ Expiry Buckets (0–30, 31–60, 61–90 days) ---
export function computeExpiryBuckets(items) {
  const now = new Date();
  const buckets = {
    Expired: 0,
    "0–30 days": 0,
    "31–60 days": 0,
    "61–90 days": 0,
  };

  items.forEach((it) => {
    if (!it.expiryDate) return;
    const diffDays = Math.ceil(
      (new Date(it.expiryDate) - now) / (1000 * 60 * 60 * 24)
    );
    if (diffDays < 0) buckets["Expired"]++;
    else if (diffDays <= 30) buckets["0–30 days"]++;
    else if (diffDays <= 60) buckets["31–60 days"]++;
    else if (diffDays <= 90) buckets["61–90 days"]++;
  });

  return Object.entries(buckets).map(([range, count]) => ({ range, count }));
}

// --- 9️⃣ Previous Month Stats + Deltas ---
export function computePrevMonthStats(items, transactions) {
  const now = new Date();
  const prev = new Date(now);
  prev.setMonth(now.getMonth() - 1);

  const prevTxns = transactions.filter((t) => {
    const d = new Date(t.created_on);
    return (
      d.getMonth() === prev.getMonth() && d.getFullYear() === prev.getFullYear()
    );
  });

  const prevItems = items.filter((i) => {
    const d = new Date(i.created_on);
    return (
      d.getMonth() === prev.getMonth() && d.getFullYear() === prev.getFullYear()
    );
  });

  const stockIn = prevTxns
    .filter((t) => t.type === "Stock in")
    .reduce((sum, t) => sum + (t.quantity || 0), 0);

  const stockOut = prevTxns
    .filter((t) => t.type === "Stock out")
    .reduce((sum, t) => sum + (t.quantity || 0), 0);

  const stats = {
    totalStockIn: stockIn,
    totalStockOut: stockOut,
    transactions: prevTxns.length,
    totalItems: prevItems.length,
  };

  return { prevTxns, prevItems, stats };
}

export function computeDeltas(curr, prev) {
  const getDelta = (curr, prev) =>
    prev === 0 ? 100 : (((curr - prev) / prev) * 100).toFixed(1);

  return {
    items: getDelta(curr.items, prev.items),
    stockIn: getDelta(curr.stockIn, prev.stockIn),
    stockOut: getDelta(curr.stockOut, prev.stockOut),
    transactions: getDelta(curr.transactions, prev.transactions),
  };
}
